import {createSlice} from '@reduxjs/toolkit'
import type {PayloadAction} from '@reduxjs/toolkit'
import undoable, {includeAction} from 'redux-undo'
import type {
  CustomDashboardInterface,
  DashboardLayout,
  DashboardTab,
  WidgetConfig,
} from '@interfaces/customDashboard'
import {
  activeTabIndex,
  duplicateTab as copyOfTab,
} from '@containers/Dashboards/CustomDashboards/utils/dashboardTabs'

interface CustomDashboardState {
  activeDashboard: CustomDashboardInterface | null
  editMode: boolean
  selectedWidgetId: string | null
  unsavedChanges: boolean
  widgetLibraryOpen: boolean
  // The open sheet (AB#6448); null without tabs. Lives in the undoable state on
  // purpose: undoing a change on another tab goes back to that tab with it.
  activeTabId: string | null
}

// The grid the editor works on: the open tab's, or the dashboard's own without
// tabs. Returned as the (immer) draft itself so reducers can mutate it.
const editedLayouts = (state: CustomDashboardState): CustomDashboardInterface['layouts'] | null => {
  const dashboard = state.activeDashboard
  if (!dashboard) return null
  const tab = dashboard.tabs?.[activeTabIndex(dashboard, state.activeTabId)]
  const layouts = tab ? tab.layouts : dashboard.layouts
  if (!layouts.lg) layouts.lg = []
  return layouts
}

const editedTab = (state: CustomDashboardState): DashboardTab | null => {
  const dashboard = state.activeDashboard
  if (!dashboard) return null
  return dashboard.tabs?.[activeTabIndex(dashboard, state.activeTabId)] ?? null
}

const withoutWidget = (
  layouts: CustomDashboardInterface['layouts'],
  widgetId: string,
): CustomDashboardInterface['layouts'] => ({
  ...layouts,
  lg: (layouts.lg ?? []).filter((layout) => layout.i !== widgetId),
  ...(layouts.md ? {md: layouts.md.filter((layout) => layout.i !== widgetId)} : {}),
  ...(layouts.sm ? {sm: layouts.sm.filter((layout) => layout.i !== widgetId)} : {}),
})

const initialState: CustomDashboardState = {
  activeDashboard: null,
  editMode: false,
  selectedWidgetId: null,
  unsavedChanges: false,
  widgetLibraryOpen: false,
  activeTabId: null,
}

const customDashboardSlice = createSlice({
  name: 'customDashboard',
  initialState,
  reducers: {
    setActiveDashboard: (state, action: PayloadAction<CustomDashboardInterface | null>) => {
      if (action.payload) {
        const tabs = (action.payload.tabs ?? []).map((tab) => ({
          ...tab,
          layouts: tab.layouts?.lg ? tab.layouts : {...tab.layouts, lg: []},
          widgetIds: tab.widgetIds ?? [],
        }))
        state.activeDashboard = {
          ...action.payload,
          layouts: action.payload.layouts ?? {lg: []},
          widgets: action.payload.widgets ?? {},
          tabs,
        }
        // Keep the open sheet across a reload of the same dashboard (save,
        // discard); anything else starts on the first one.
        if (!tabs.some((tab) => tab.id === state.activeTabId)) {
          state.activeTabId = tabs[0]?.id ?? null
        }
      } else {
        state.activeDashboard = null
        state.activeTabId = null
      }
      state.unsavedChanges = false
      state.editMode = false
      state.selectedWidgetId = null
    },
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.editMode = action.payload
      state.widgetLibraryOpen = action.payload
      if (!action.payload) {
        state.selectedWidgetId = null
      }
    },
    setSelectedWidget: (state, action: PayloadAction<string | null>) => {
      state.selectedWidgetId = action.payload
    },
    updateLayout: (
      state,
      action: PayloadAction<{breakpoint: 'lg' | 'md' | 'sm'; layouts: DashboardLayout[]}>,
    ) => {
      const layouts = editedLayouts(state)
      if (layouts) {
        layouts[action.payload.breakpoint] = action.payload.layouts
        state.unsavedChanges = true
      }
    },
    addWidget: (state, action: PayloadAction<{layout: DashboardLayout; config: WidgetConfig}>) => {
      const layouts = editedLayouts(state)
      if (state.activeDashboard && layouts) {
        layouts.lg.push(action.payload.layout)
        editedTab(state)?.widgetIds.push(action.payload.config.id)
        state.activeDashboard.widgets[action.payload.config.id] = action.payload.config
        state.unsavedChanges = true
        state.selectedWidgetId = action.payload.config.id
      }
    },
    updateWidgetConfig: (
      state,
      action: PayloadAction<{id: string; config: Partial<WidgetConfig>}>,
    ) => {
      if (state.activeDashboard?.widgets[action.payload.id]) {
        state.activeDashboard.widgets[action.payload.id] = {
          ...state.activeDashboard.widgets[action.payload.id],
          ...action.payload.config,
          // Legacy widgets can be missing `id` inside their own config
          // (only the dict key had it) -- backfill it on every edit so the
          // gap self-heals once saved (AB#6230).
          id: action.payload.id,
        }
        state.unsavedChanges = true
      }
    },
    updateWidgetLayout: (
      state,
      action: PayloadAction<{id: string; layout: Partial<DashboardLayout>}>,
    ) => {
      const layouts = editedLayouts(state)
      if (layouts) {
        const layoutIndex = layouts.lg.findIndex((layout) => layout.i === action.payload.id)
        if (layoutIndex !== -1) {
          layouts.lg[layoutIndex] = {
            ...layouts.lg[layoutIndex],
            ...action.payload.layout,
          }
          state.unsavedChanges = true
        }
      }
    },
    removeWidget: (state, action: PayloadAction<string>) => {
      if (state.activeDashboard) {
        state.activeDashboard.layouts = withoutWidget(state.activeDashboard.layouts, action.payload)
        state.activeDashboard.tabs = (state.activeDashboard.tabs ?? []).map((tab) => ({
          ...tab,
          layouts: withoutWidget(tab.layouts, action.payload),
          widgetIds: tab.widgetIds.filter((id) => id !== action.payload),
        }))
        delete state.activeDashboard.widgets[action.payload]
        state.unsavedChanges = true
        if (state.selectedWidgetId === action.payload) {
          state.selectedWidgetId = null
        }
      }
    },
    duplicateWidget: (state, action: PayloadAction<string>) => {
      const layouts = editedLayouts(state)
      if (state.activeDashboard && layouts) {
        const sourceWidget = state.activeDashboard.widgets[action.payload]
        const sourceLayout = layouts.lg.find((layout) => layout.i === action.payload)

        if (sourceWidget && sourceLayout) {
          const newId = `widget-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

          // Create new widget config (deep copy settings)
          const newWidget: WidgetConfig = {
            ...sourceWidget,
            id: newId,
            title: sourceWidget.title ? `${sourceWidget.title} (Copy)` : undefined,
            settings: JSON.parse(JSON.stringify(sourceWidget.settings || {})),
          }

          // Create new layout positioned below the original
          const newLayout: DashboardLayout = {
            ...sourceLayout,
            i: newId,
            y: sourceLayout.y + sourceLayout.h,
          }

          state.activeDashboard.widgets[newId] = newWidget
          layouts.lg.push(newLayout)
          editedTab(state)?.widgetIds.push(newId)
          state.unsavedChanges = true
          state.selectedWidgetId = newId
        }
      }
    },
    setUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.unsavedChanges = action.payload
    },
    setWidgetLibraryOpen: (state, action: PayloadAction<boolean>) => {
      state.widgetLibraryOpen = action.payload
    },
    updateDashboardSettings: (state, action: PayloadAction<Partial<CustomDashboardInterface>>) => {
      if (state.activeDashboard) {
        state.activeDashboard = {
          ...state.activeDashboard,
          ...action.payload,
        }
        state.unsavedChanges = true
      }
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTabId = action.payload
      state.selectedWidgetId = null
    },
    // The first tab added to a dashboard without tabs also turns its current
    // grid into a named first tab, so nothing on it moves.
    addTab: (
      state,
      action: PayloadAction<{id: string; name: string; firstTab?: {id: string; name: string}}>,
    ) => {
      const dashboard = state.activeDashboard
      if (!dashboard) return
      const tabs = dashboard.tabs?.length ? dashboard.tabs : []
      if (tabs.length === 0) {
        if (!action.payload.firstTab) return
        tabs.push({
          ...action.payload.firstTab,
          layouts: dashboard.layouts?.lg ? dashboard.layouts : {lg: []},
          widgetIds: Object.keys(dashboard.widgets ?? {}),
        })
        dashboard.tabs = tabs
      }
      tabs.push({
        id: action.payload.id,
        name: action.payload.name,
        layouts: {lg: []},
        widgetIds: [],
      })
      state.activeTabId = action.payload.id
      state.selectedWidgetId = null
      state.unsavedChanges = true
    },
    // A copy of a sheet right after it, with new widget ids (AB#6648); opens it.
    duplicateTab: (state, action: PayloadAction<{sourceId: string; id: string; name: string}>) => {
      const dashboard = state.activeDashboard
      if (!dashboard) return
      const copy = copyOfTab(dashboard, action.payload.sourceId, {
        id: action.payload.id,
        name: action.payload.name,
      })
      if (!copy) return
      dashboard.tabs = copy.tabs
      dashboard.widgets = copy.widgets
      state.activeTabId = action.payload.id
      state.selectedWidgetId = null
      state.unsavedChanges = true
    },
    renameTab: (state, action: PayloadAction<{id: string; name: string}>) => {
      const tab = state.activeDashboard?.tabs?.find((t) => t.id === action.payload.id)
      if (tab && action.payload.name.trim()) {
        tab.name = action.payload.name.trim()
        state.unsavedChanges = true
      }
    },
    moveTab: (state, action: PayloadAction<{id: string; offset: -1 | 1}>) => {
      const tabs = state.activeDashboard?.tabs
      if (!tabs) return
      const from = tabs.findIndex((t) => t.id === action.payload.id)
      const to = from + action.payload.offset
      if (from === -1 || to < 0 || to >= tabs.length) return
      const [tab] = tabs.splice(from, 1)
      tabs.splice(to, 0, tab)
      state.unsavedChanges = true
    },
    // Deletes the tab with its widgets. Down to one tab, the dashboard goes
    // back to having no tabs: that tab's grid becomes the dashboard's grid.
    deleteTab: (state, action: PayloadAction<string>) => {
      const dashboard = state.activeDashboard
      const tabs = dashboard?.tabs
      if (!dashboard || !tabs || tabs.length < 2) return
      const index = tabs.findIndex((t) => t.id === action.payload)
      if (index === -1) return
      const [removed] = tabs.splice(index, 1)
      removed.widgetIds.forEach((id) => {
        delete dashboard.widgets[id]
      })
      if (tabs.length === 1) {
        dashboard.layouts = tabs[0].layouts
        dashboard.tabs = []
        state.activeTabId = null
      } else if (state.activeTabId === removed.id) {
        state.activeTabId = tabs[Math.min(index, tabs.length - 1)].id
      }
      state.selectedWidgetId = null
      state.unsavedChanges = true
    },
    resetDashboardState: () => {
      return initialState
    },
  },
})

export const {
  setActiveDashboard,
  setEditMode,
  setSelectedWidget,
  updateLayout,
  addWidget,
  updateWidgetConfig,
  updateWidgetLayout,
  removeWidget,
  duplicateWidget,
  setUnsavedChanges,
  setWidgetLibraryOpen,
  updateDashboardSettings,
  setActiveTab,
  addTab,
  duplicateTab,
  renameTab,
  moveTab,
  deleteTab,
  resetDashboardState,
} = customDashboardSlice.actions

// In-memory undo/redo (AB#6079) -- instant Ctrl+Z over the widgets/layout a
// user is editing. Inclusion-based on purpose: a future reducer that edits
// content has to be added here to become undoable, rather than silently
// joining the history because nobody added it to an exclusion list.
const CONTENT_ACTIONS = [
  updateLayout.type,
  addWidget.type,
  updateWidgetConfig.type,
  updateWidgetLayout.type,
  removeWidget.type,
  duplicateWidget.type,
  updateDashboardSettings.type,
  addTab.type,
  duplicateTab.type,
  renameTab.type,
  moveTab.type,
  deleteTab.type,
]

// `clearHistoryType` is NOT how this is done: redux-undo intercepts a
// matching action before it ever reaches the wrapped reducer, resetting
// history but leaving `present` untouched -- exactly wrong for an action like
// `setActiveDashboard` whose entire job IS to change `present`. Callers that
// load a different dashboard (or discard back to the live record) instead
// dispatch `ActionCreators.clearHistory()` as a SEPARATE, following action, so
// a previous dashboard's edits never sit in the undo stack for this one to
// inherit -- see `setActiveDashboard`'s call sites.
export default undoable(customDashboardSlice.reducer, {
  limit: 50,
  filter: includeAction(CONTENT_ACTIONS),
})
